///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

//-----------------------------------------------------------------------------------------
//* \brief
//? Front-end half of the CUI lookup: calls the backend and translates what the registers
//? answer into the exact strings the app's own dropdowns use.
//?
//? The registers write county and city in their own style ("MUNICIPIUL BRAȘOV",
//? "BISTRIȚA-NĂSĂUD") while RoCities holds them without diacritics and spaced differently
//? ("Brasov", "Bistrita - Nasaud"). Handing the raw value straight to the form would put a
//? county in the field that the select cannot represent, so everything is matched back
//? onto the canonical list first.
//-----------------------------------------------------------------------------------------

import { companyLookupUrlString } from "./ValidationService";
import { cities, counties } from "../databases/RoCities";

//? Administrative prefixes the registers put in front of a locality or county name and
//? that never appear in RoCities.
const PLACE_PREFIXES = [
  "MUNICIPIULUI",
  "MUNICIPIUL",
  "MUN",
  "ORASUL",
  "ORAS",
  "COMUNA",
  "JUDETUL",
  "JUDET",
  "JUD",
  "SATUL",
  "SAT"
];

//-----------------------------------------------------------------------------------------
//* \brief
//? Reduces a place name to something comparable: no diacritics, no punctuation, no
//? spacing, no administrative prefix. "Bistrița - Năsăud" and "BISTRITA-NASAUD" both
//? collapse to "BISTRITANASAUD".
//-----------------------------------------------------------------------------------------
const normalizePlace = (value) => {
  if (!value) return "";
  let text = value
    .toString()
    .normalize("NFD")
    //? Covers the comma-below (ș, ț) and cedilla (ş, ţ) spellings alike - both decompose
    //? into a base letter plus a mark in this range.
    // eslint-disable-next-line no-misleading-character-class
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase();

  PLACE_PREFIXES.forEach((prefix) => {
    text = text.replace(new RegExp(`\\b${prefix}\\b\\.?`, "g"), " ");
  });

  return text.replace(/[^A-Z0-9]/g, "");
};

//-----------------------------------------------------------------------------------------
//* \brief
//? Finds the entry of `list` that means the same place as `value`, or '' when there is
//? none. Returning '' on purpose: an unmatched county left in the field would be a value
//? the select cannot show, which reads to the user as the form silently losing their data.
//-----------------------------------------------------------------------------------------
const matchInList = (value, list) => {
  const wanted = normalizePlace(value);
  if (wanted === "") return "";
  const hit = list.find((entry) => normalizePlace(entry) === wanted);
  return hit || "";
};

export const matchCounty = (value) => {
  const direct = matchInList(value, counties);
  if (direct !== "") return direct;
  //? Bucharest arrives as "MUNICIPIUL BUCUREȘTI", as a sector, or as "ILFOV/BUCURESTI"
  //? depending on the register and the year the company was registered.
  if (normalizePlace(value).includes("BUCURESTI")) return "Bucuresti";
  return "";
};

export const matchCity = (value) => {
  const direct = matchInList(value, cities);
  if (direct !== "") return direct;
  if (normalizePlace(value).includes("BUCURESTI")) return matchInList("Bucuresti", cities);
  //? Not in our list is not a reason to throw the answer away - the city field is free
  //? text, so the register's spelling is better than nothing.
  return value ? value.toString().trim() : "";
};

//-----------------------------------------------------------------------------------------
//* \brief
//? Looks a CUI up and returns it shaped for the registration form.
//?
//? Never throws and never rejects: every failure comes back as `{ found: false }` with a
//? reason, because not finding a company must leave the user typing the fields by hand
//? rather than staring at an error.
//?
//? @return { found, source, name, regCom, address, city, county, status, reason }
//-----------------------------------------------------------------------------------------
export const lookupCompany = async (cui) => {
  if (!cui || cui.toString().trim() === "") {
    return { found: false, reason: "invalid" };
  }

  try {
    const response = await fetch(companyLookupUrlString(cui.toString().trim()));
    if (!response.ok) return { found: false, reason: "unavailable" };

    const data = await response.json();
    if (!data || !data.found) {
      return { found: false, reason: data && data.reason ? data.reason : "notfound" };
    }

    return {
      found: true,
      source: data.source || "",
      name: (data.name || "").trim(),
      regCom: (data.regCom || "").trim(),
      address: (data.address || "").trim(),
      county: matchCounty(data.county),
      city: matchCity(data.city),
      status: (data.status || "").trim(),
      //? ANAF publishes an explicit flag; the free-text status is only read when the flag
      //? is absent, which is the case for every VIES answer.
      inactive: data.inactive === true || isInactiveStatus(data.status)
    };
  }
  catch (err) {
    return { found: false, reason: "unavailable" };
  }
};

//-----------------------------------------------------------------------------------------
//* \brief
//? True when the register says the company is not currently active. Shown as a warning
//? next to the confirmed details - it is never a reason to refuse a registration.
//-----------------------------------------------------------------------------------------
export const isInactiveStatus = (status) => {
  if (!status) return false;
  const text = normalizePlace(status);
  return text.includes("INACTIV") || text.includes("RADIAT") || text.includes("SUSPENDAT");
};
